import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/apiClient";
import { users as usersApi, type MyActivityRow, type UserDetail } from "@/features/users/api/users.api";
import { useAuth } from "@/features/auth/auth";
import { USER_ROLE_TIER_LABELS } from "@/features/auth/api/auth.api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { PasswordField } from "@/components/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Dash } from "@/components/Dash";
import { formatDateTime } from "@/utils/dates";

const passwordSchema = z.object({
  current: z.string().min(1, "Enter your current password."),
  next: z.string().min(6, "New password must be at least 6 characters."),
  confirm: z.string().min(1, "Confirm your new password."),
}).superRefine((v, ctx) => {
  if (v.next && v.confirm && v.next !== v.confirm) {
    ctx.addIssue({ code: "custom", path: ["confirm"], message: "New password and confirmation don't match." });
  }
  if (v.next && v.current && v.next === v.current) {
    ctx.addIssue({ code: "custom", path: ["next"], message: "New password must be different from the current one." });
  }
});
type PasswordForm = z.infer<typeof passwordSchema>;

export default function MyAccountPage() {
  const { user } = useAuth();
  const [me, setMe] = useState<UserDetail | null>(null);
  const [feedback, setFeedback] = useState<
    { kind: "ok" | "err"; msg: string } | null
  >(null);

  const [activity, setActivity] = useState<MyActivityRow[] | null>(null);

  const form = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current: "", next: "", confirm: "" },
  });

  useEffect(() => {
    let cancelled = false;
    usersApi.getMe()
      .then((d) => { if (!cancelled) setMe(d); })
      .catch(() => {  });
    usersApi.myActivity(20)
      .then((d) => { if (!cancelled) setActivity(d); })
      .catch(() => { if (!cancelled) setActivity([]); });
    return () => { cancelled = true; };
  }, []);

  async function changePassword(v: PasswordForm) {
    setFeedback(null);
    try {
      await usersApi.changeMyPassword(v.current, v.next);
      setFeedback({ kind: "ok", msg: "Password updated. Use the new password next time you sign in." });
      form.reset({ current: "", next: "", confirm: "" });
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message :
        e instanceof Error ? e.message :
        "Couldn't change your password.";
      setFeedback({ kind: "err", msg });
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">My account</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as <span className="font-medium text-foreground">{user?.name}</span>
          {user?.email ? <> &middot; {user.email}</> : null}
          {user?.role ? <> &middot; {USER_ROLE_TIER_LABELS[user.role]}</> : null}
        </p>
        {me?.lastLoginAt && (
          <p className="text-xs text-muted-foreground mt-2">
            Last signed in: {formatDateTime(me.lastLoginAt)}
            {me.lastLoginIp ? <> from <span className="font-mono">{me.lastLoginIp}</span></> : null}.
            If that doesn't match what you remember, change your password below.
          </p>
        )}
      </header>

      <Card>
        <CardHeader className="border-b py-3">
          <CardTitle className="text-base font-semibold text-slate-900">Change password</CardTitle>
          <CardDescription>
            Enter your current password, then the new one twice. Minimum 6
            characters; the new password must differ from the current one.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(changePassword)}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <PasswordField control={form.control} name="current" label="Current password" autoComplete="current-password" />
                <PasswordField control={form.control} name="next" label="New password" autoComplete="new-password" />
                <PasswordField control={form.control} name="confirm" label="Confirm new password" autoComplete="new-password" />
              </div>

              {feedback && (
                <Alert
                  variant={feedback.kind === "err" ? "destructive" : "default"}
                  className={cn(
                    feedback.kind === "ok" &&
                      "border-emerald-200 bg-emerald-50 text-emerald-800",
                  )}
                >
                  <AlertDescription>{feedback.msg}</AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-end">
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? "Saving…" : "Update password"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <NotificationPrefsCard me={me} onUpdated={(d) => setMe(d)} />

      <Card className="mt-8">
        <CardHeader className="border-b py-3">
          <CardTitle className="text-base font-semibold text-slate-900">Recent activity</CardTitle>
          <CardDescription>
            The last 20 things you've done in the portal — sign-ins,
            settings changes, edits. If you see something here you don't
            recognize, change your password.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ActivityList rows={activity} />
        </CardContent>
      </Card>
    </div>
  );
}

function ActivityList({ rows }: { rows: MyActivityRow[] | null }) {
  if (rows === null) {
    return <Loading className="p-6">Loading…</Loading>;
  }
  if (rows.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        Nothing recorded yet. Activity will appear here as you use the portal.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs font-medium uppercase tracking-wide">When</TableHead>
          <TableHead className="text-xs font-medium uppercase tracking-wide">Action</TableHead>
          <TableHead className="text-xs font-medium uppercase tracking-wide">Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.occurredAt}-${i}`} className="align-top">
            <TableCell className="text-foreground tabular-nums whitespace-nowrap">
              {formatDateTime(r.occurredAt)}
            </TableCell>
            <TableCell className="font-mono text-xs">
              {r.action}
            </TableCell>
            <TableCell className="text-muted-foreground text-xs">
              {r.entityType ? `${r.entityType}${r.entityId ? ` · ${r.entityId.slice(0, 8)}` : ""}` : <Dash />}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function NotificationPrefsCard({
  me,
  onUpdated,
}: {
  me: UserDetail | null;
  onUpdated: (next: UserDetail) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  if (me === null) return null;

  async function toggle(next: boolean) {
    setSaving(true);
    setFeedback(null);
    try {
      await usersApi.updateMyNotificationPrefs(next);

      onUpdated({ ...me!, notifyOnTicketAssign: next });
      setFeedback({
        kind: "ok",
        msg: next
          ? "You'll get an email when a ticket is assigned to you."
          : "Ticket-assignment emails are now muted.",
      });
    } catch (e) {
      setFeedback({
        kind: "err",
        msg: e instanceof ApiError ? e.message : "Couldn't save your preference.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mt-8">
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">Notifications</CardTitle>
        <CardDescription>
          Pick which courtesy emails you want from the portal. Other admins can
          still assign you tickets either way — this only controls the email.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <label className="flex items-start gap-3 text-sm text-foreground">
          <Checkbox
            checked={me.notifyOnTicketAssign}
            disabled={saving}
            onCheckedChange={(checked) => toggle(checked === true)}
            className="mt-0.5"
          />
          <span>
            Email me when a ticket is assigned to me
            <span className="block text-xs text-muted-foreground">
              Includes ticket subject, priority, due date, and a direct link.
            </span>
          </span>
        </label>
        {feedback && (
          <p
            className={cn(
              "text-sm mt-3",
              feedback.kind === "ok" ? "text-emerald-700" : "text-destructive",
            )}
          >
            {feedback.msg}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
